import { useMutation } from '@apollo/client'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@chain-zen/ui'
import { useState } from 'react'
import { toast } from 'sonner'

import { useShipmentContext } from '../../../contexts/shipment-context'
import { SEND_APP_DOWNLOAD_LINK } from '../../../data/mutations/tracking'
import { SHIPMENT_TRACKING_SOURCES } from '../../../data/queries/tracking'
import { useUserQuery } from '../../../hooks/use-user-query'
import type { TShipmentMember } from '../../../types/shipment'
import type { TMobileAppTrackingSource } from '../../../types/tracking'
import { TrackingSourceRow } from '../tracking-source-row'
import { isDeviceTracking } from '../utils'
import { getLatestAppDownloadAction, normalizeAppDownloadActions } from './app-download-actions'
import { AppDownloadStatusButton } from './app-download-status-button'
import { DeviceInfo } from './device-info'
import { MobileAppDeviceTooltip } from './mobile-app-tooltip'
import { SendAppDownloadLinkButton } from './send-app-download-link'

interface MobileAppTrackingSourceProps {
  drivers?: TShipmentMember[]
  shipmentID: string
  shipmentNumber: string
  shipmentOwnerWorkspaceID?: string
  trackingSourceData: TMobileAppTrackingSource
}

type TDeviceTracking =
  | {
      isTracking: boolean
      reason: string
    }
  | {
      isTracking: boolean
      reason?: undefined
    }

const getAppDownloadActions = (trackingSourceData: TMobileAppTrackingSource) => {
  const rawActions = trackingSourceData.metadata?.appDownloadActions

  if (!rawActions) return []

  // Ensure we always return an array, even if the backend sends a different format
  return Array.isArray(rawActions) ? rawActions : []
}

/**
 * This component is used to display the mobile app tracking source information.
 */
export const MobileAppTrackingSource = (props: MobileAppTrackingSourceProps) => {
  const { trackingSourceData, shipmentID, shipmentNumber, drivers, shipmentOwnerWorkspaceID } = props
  const { isShipmentOwner } = useShipmentContext()
  const { myDetails } = useUserQuery()
  const [useCompactActionButtons, setUseCompactActionButtons] = useState(false)
  const [stackActionsBelowDetails, setStackActionsBelowDetails] = useState(false)
  const locations = trackingSourceData.locations || []

  const lastLocation = locations.length > 0 ? locations[locations.length - 1] : null

  const lastMotionActivity = lastLocation?.locationInfo?.activity?.type || null

  const lastSeen = trackingSourceData.metadata?.lastSeen || null
  const lastTrackingStoppedAt = trackingSourceData.metadata?.lastTrackingStoppedAt || null

  // Find the matching driver based on phone number
  const matchingDriver = drivers?.find((driver: TShipmentMember) => {
    const driverPhone = driver.user.phone
    const trackingPhone = trackingSourceData.metadata?.user?.phone
    return driverPhone && trackingPhone && driverPhone === trackingPhone
  })

  const phoneNumberInfo = matchingDriver?.user.phoneNumberInfo

  const latestLocationData = trackingSourceData.metadata?.lastLocationData || lastLocation || null

  const getCanSendAppDownloadLink = () => {
    if (!trackingSourceData.metadata?.notificationAlert) {
      return false
    }
    if (trackingSourceData.metadata?.notificationAlert.appDownload) {
      return trackingSourceData.metadata.notificationAlert.appDownload.numberOfTimesSent < 5
    }
    return true
  }

  const canSendAppDownloadLink = getCanSendAppDownloadLink()

  const deviceTracking: TDeviceTracking = isDeviceTracking({
    authorizationStatus: trackingSourceData.metadata?.locationSettings?.authorizationStatus,
    hasSentMockLocations: trackingSourceData.metadata?.hasSentMockLocations || false,
    isHistoricalDevice: trackingSourceData.metadata?.isHistoricalDevice || false,
    isShipmentOwner,
    lastLocationTimestamp: lastLocation?.timestamp || null
  })

  const [sendAppDownloadLink, { loading: sendingAppDownloadLink }] = useMutation(SEND_APP_DOWNLOAD_LINK, {
    onCompleted: () => {
      toast.success('Download link sent successfully.')
    },
    onError: (error) => {
      toast.error(error.message ?? 'Something went wrong.')
    },
    refetchQueries: [SHIPMENT_TRACKING_SOURCES, 'TrackingSources']
  })

  const handleSendDownloadAppLink = () => {
    const input = {
      shipmentID: shipmentID,
      shipmentNumber: shipmentNumber,
      shipmentOwnerWorkspaceID: shipmentOwnerWorkspaceID,
      userID: trackingSourceData.metadata?.user.id
    }

    sendAppDownloadLink({ variables: input })
  }

  const appDownloadActions = normalizeAppDownloadActions(getAppDownloadActions(trackingSourceData))

  const latestAction = getLatestAppDownloadAction(appDownloadActions)

  return (
    <div className="w-full">
      <TrackingSourceRow
        className="px-2"
        onStackChange={(isStacked) => {
          setStackActionsBelowDetails(isStacked)
          setUseCompactActionButtons(isStacked)
        }}
        actions={
          !!myDetails && (
            <>
              <SendAppDownloadLinkButton
                canSendAppDownloadLink={canSendAppDownloadLink}
                compact={useCompactActionButtons}
                onSendDownloadAppLink={handleSendDownloadAppLink}
                sendingAppDownloadLink={sendingAppDownloadLink}
              />
              <AppDownloadStatusButton
                appDownloadActions={appDownloadActions}
                latestAction={latestAction}
                reserveSpace={!stackActionsBelowDetails}
              />
            </>
          )
        }
      >
        <HoverCard>
          <HoverCardTrigger asChild={true}>
            <div className="min-w-0 flex-1 cursor-pointer">
              <DeviceInfo
                deviceInfo={trackingSourceData.metadata?.deviceInfo || null}
                hasSentMockLocations={trackingSourceData.metadata?.hasSentMockLocations || false}
                hasHistoricalDeviceMetadata={!!trackingSourceData.metadata?.deviceInfo}
                isDeviceTracking={deviceTracking}
                isHistoricalDevice={trackingSourceData.metadata?.isHistoricalDevice || false}
                lastSeen={lastSeen}
                lastLocationCity={latestLocationData?.city || null}
                lastLocationStateCode={latestLocationData?.stateCode || null}
                lastLocationTimestamp={latestLocationData?.timestamp || null}
                latestAction={latestAction}
                locationSettings={trackingSourceData.metadata?.locationSettings || null}
                isLoggedIn={trackingSourceData.metadata?.isLoggedIn}
                isTrackingOn={trackingSourceData.metadata?.isTrackingOn}
                phoneNumberInfo={phoneNumberInfo}
                userName={trackingSourceData.metadata?.user.name || null}
                userPhone={trackingSourceData.metadata?.user?.phone || null}
                userPhoto={trackingSourceData.metadata?.user?.photo || null}
              />
            </div>
          </HoverCardTrigger>
          <HoverCardContent
            className="w-max max-w-sm text-sm max-h-100 h-max overflow-y-auto"
            side="top"
            align="center"
            sideOffset={4}
          >
            <MobileAppDeviceTooltip
              device={trackingSourceData.metadata?.deviceInfo ? trackingSourceData.metadata?.deviceInfo : null}
              driverName={trackingSourceData.metadata?.user.name || null}
              hasSentMockLocations={trackingSourceData.metadata?.hasSentMockLocations || false}
              isHistoricalDevice={trackingSourceData.metadata?.isHistoricalDevice || false}
              lastMotionActivity={lastMotionActivity}
              lastSeen={lastSeen}
              lastTrackingStoppedAt={lastTrackingStoppedAt}
              locationSettings={trackingSourceData.metadata?.locationSettings || null}
              phoneNumberInfo={phoneNumberInfo || null}
              isLoggedIn={trackingSourceData.metadata?.isLoggedIn}
              isTrackingOn={trackingSourceData.metadata?.isTrackingOn}
            />
          </HoverCardContent>
        </HoverCard>
      </TrackingSourceRow>
    </div>
  )
}
